/**
 * Fetch with exponential backoff retry logic
 * Industry standard approach for handling network failures
 */

interface FetchWithRetryOptions extends RequestInit {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

export async function fetchWithRetry(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    onRetry,
    ...fetchOptions
  } = options;

  let lastError: Error | null = null;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Log attempt details
      console.log(`[FetchRetry] Attempt ${attempt + 1}/${maxRetries + 1}`, {
        url,
        method: fetchOptions.method || 'GET',
        hasHeaders: !!fetchOptions.headers,
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch(url, fetchOptions);
      
      // Check if response is ok or if it's a client error that shouldn't be retried
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        console.log(`[FetchRetry] Success on attempt ${attempt + 1}`, {
          url,
          status: response.status,
          statusText: response.statusText
        });
        return response;
      }
      
      // For server errors (5xx), throw to trigger retry
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
      
    } catch (error) {
      lastError = error as Error;
      
      // Enhanced error logging
      const errorDetails = {
        url,
        attempt: attempt + 1,
        error: lastError?.message,
        errorType: error?.constructor?.name,
        errorStack: lastError?.stack?.split('\n').slice(0, 3).join('\n'),
        isCorsError: lastError?.message?.includes('CORS'),
        isNetworkError: lastError?.message?.includes('Failed to fetch'),
        isTypeError: error instanceof TypeError,
        timestamp: new Date().toISOString()
      };
      
      console.error('[FetchRetry] Attempt failed:', errorDetails);
      
      // Don't retry on abort
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      
      // Check if it's a CORS or network error
      const isCorsOrNetworkError = 
        error instanceof TypeError && 
        (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'));
      
      if (attempt < maxRetries) {
        if (onRetry) {
          onRetry(attempt + 1, error as Error);
        }
        
        console.warn(`[FetchRetry] Will retry in ${delay}ms...`, {
          url,
          nextAttempt: attempt + 2,
          maxRetries: maxRetries + 1,
          isCorsOrNetworkError
        });
        
        // Wait with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Increase delay for next attempt
        delay = Math.min(delay * backoffFactor, maxDelay);
      }
    }
  }
  
  // All retries failed
  throw lastError || new Error('Fetch failed after maximum retries');
}

/**
 * Helper to check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // Network errors are retryable
    return error.message.includes('Failed to fetch') || 
           error.message.includes('NetworkError');
  }
  
  if (error instanceof Error) {
    // Timeout errors are retryable
    return error.name === 'TimeoutError' || 
           error.message.includes('timeout');
  }
  
  return false;
}